import React, { useState } from 'react';
import { Shield } from 'lucide-react';
import { useTheme } from '../ThemeContext';
import { useEffect } from 'react';
import { ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';

const FAQ = () => {
    const { isDark } = useTheme();
    const [expandedIndex, setExpandedIndex] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');

    const faqItems = [
        // Connexion & Sécurité
        {
            question: "Pourquoi avoir choisi une authentification par email plutôt qu'un système de mot de passe classique ?",
            answer: "La vérification par email offre un niveau de sécurité supplémentaire tout en simplifiant l'expérience utilisateur. Pas besoin de mémoriser un mot de passe, et chaque connexion est sécurisée par un code unique envoyé sur votre email Solganeo."
        },
        {
            question: "Combien de temps le code de vérification par email reste-t-il valide ?",
            answer: "Le code de vérification envoyé par email est valable pendant 5 minutes pour des raisons de sécurité. Si le code expire, vous pouvez facilement en demander un nouveau."
        },
        {
            question: "Que faire si je ne reçois pas le code de vérification par email ?",
            answer: "Vérifiez d'abord vos spams. Si vous ne trouvez toujours pas le code, utilisez le bouton 'Renvoyer le code' sur la page de connexion. Si le problème persiste, contactez vos managers."
        },

        // Utilisation Quotidienne
        {
            question: "Comment ajouter un nouveau service d'authentification ?",
            answer: "Cliquez sur le bouton 'Ajouter' en bas de page. Vous pourrez alors entrer les informations du service et scanner un QR code ou entrer manuellement la clé secrète. Le service sera immédiatement disponible dans votre tableau de bord."
        },
        {
            question: "Comment fonctionne la barre de progression à côté de chaque code ?",
            answer: "Cette barre indique le temps restant avant le changement du code. Un cycle complet dure 30 secondes. Quand la barre est presque vide, préparez-vous à utiliser le nouveau code qui va être généré."
        },
        {
            question: "Comment copier rapidement un code ?",
            answer: "Cliquez sur l'icône de copie à droite du code. Une animation verte confirmera la copie réussie. Le code est alors dans votre presse-papiers, prêt à être collé."
        },

        // Personnalisation
        {
            question: "Puis-je personnaliser l'apparence des services ?",
            answer: "Oui, lors de l'édition d'un service, vous pouvez choisir parmi différentes couleurs (bleu, rouge, vert, violet, orange) pour personnaliser l'apparence de la carte."
        },
        {
            question: "Comment activer le mode sombre ?",
            answer: "Cliquez sur l'icône lune/soleil en haut de page pour basculer entre les modes clair et sombre. Votre préférence sera sauvegardée pour vos prochaines visites."
        },
        {
            question: "Comment modifier les informations d'un service existant ?",
            answer: "Cliquez sur l'icône de crayon à côté du nom du service pour ouvrir le modal d'édition. Vous pourrez modifier le nom, la description, et d'autres paramètres."
        },

        // Gestion des Accès
        {
            question: "Quels sont les différents niveaux d'accès ?",
            answer: "Il existe deux niveaux principaux : Manager et Consultant. Les managers ont accès à tous les services, tandis que les consultants n'ont accès qu'aux services de leurs deliveries spécifiques."
        },
        {
            question: "Comment sont attribués les accès aux services ?",
            answer: "Les accès sont gérés par delivery. Votre compte est automatiquement configuré avec les accès correspondant à votre rôle et à vos deliveries dans Solganeo."
        },

        // Technique & Sécurité
        {
            question: "Les codes générés sont-ils synchronisés avec toutes les applications d'authentification ?",
            answer: "Oui, l'application utilise le standard TOTP (Time-based One-Time Password), compatible avec Google Authenticator, Microsoft Authenticator, Salesforce Authenticator, Authy et autres applications similaires."
        },
        {
            question: "Que se passe-t-il si je change de téléphone ou perds mon application d'authentification ?",
            answer: "Il suffit de vous reconnecter à votre compte Solganeo. Comme l'application est connectée au CRM, vous retrouverez automatiquement tous vos services configurés."
        },
        {
            question: "Les codes sont-ils identiques pour tous les utilisateurs d'un même service ?",
            answer: "Chaque utilisateur dispose de tokens différents, mais la clé secrète du service reste la même. Cela signifie que l'authentification fonctionnera pour tous les utilisateurs ayant accès au service."
        },

        // Dépannage
        {
            question: "Que faire si un code ne fonctionne pas ?",
            answer: "Plusieurs vérifications sont possibles : 1) Vérifiez que l'heure de votre appareil est correctement synchronisée, 2) Vérifiez si l'A2F n'a pas été reconfiguré récemment, 3) Vérifiez la clé secrète dans l'outil d'édition du service. Si le problème persiste, attendez le prochain code (30 secondes)."
        },
        {
            question: "Comment savoir si ma session est toujours active ?",
            answer: "Tant que vous voyez votre tableau de bord et que les codes se régénèrent, votre session est active. En cas d'inactivité prolongée, vous serez automatiquement déconnecté."
        },
        {
            question: "L'application est-elle accessible hors du réseau Solganeo ?",
            answer: "Oui, l'application est accessible de partout. Vous aurez juste besoin d'avoir accès à votre email Solganeo pour la connexion initiale."
        }
    ];

    const filteredFaqItems = faqItems.filter(item =>
        item.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.answer.toLowerCase().includes(searchQuery.toLowerCase())
    );

    useEffect(() => {
        const user = localStorage.getItem('user');
        if (!user) {
            window.location.href = '/';
        }
    }, []);

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pb-24">
            {/* Bouton Retour */}
            <Link
                to="/"
                className="absolute top-4 left-4 p-3 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors duration-200"
                title="Retour"
            >
                <ArrowLeft className="w-7 h-7 text-gray-500" />
            </Link>
            <div className="w-full max-w-4xl mx-auto p-4 sm:p-6 lg:p-8">
                {/* Header */}
                <div className="mb-8 text-center space-y-3">
                    <div className="flex justify-center items-center gap-2">
                        <Shield className="w-8 h-8 text-blue-400" />
                        <h1 className="text-3xl sm:text-4xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-sky-400">
                            FAQ
                        </h1>
                    </div>
                    <p className="text-gray-500 dark:text-gray-400 text-lg">
                        Questions fréquemment posées
                    </p>
                    <div className="w-32 h-1 bg-gradient-to-r from-blue-400 to-sky-400 mx-auto rounded-full" />
                </div>


                {/* Search Bar */}
                <div className="mb-8">
                    <div className="relative w-full max-w-2xl mx-auto">
                        <input
                            type="text"
                            placeholder="Rechercher une question..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full px-4 py-3 pr-12 rounded-xl border focus:ring-2 focus:ring-blue-400 focus:border-transparent outline-none bg-white dark:bg-gray-800 dark:border-gray-700 text-gray-900 dark:text-white transition-colors duration-200"
                        />
                        <div className="absolute right-4 top-1/2 -translate-y-1/2">
                            <svg
                                className="w-5 h-5 text-gray-400 dark:text-gray-500"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                                />
                            </svg>
                        </div>
                    </div>
                </div>

                {/* FAQ Items */}
                <div className="space-y-4">
                    {searchQuery && filteredFaqItems.length === 0 ? (
                        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                            Aucune question trouvée pour "{searchQuery}"
                        </div>
                    ) : (
                        filteredFaqItems.map((item, index) => (
                            <div
                                key={index}
                                className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 dark:border-gray-700 overflow-hidden"
                            >
                                <button
                                    onClick={() => setExpandedIndex(expandedIndex === index ? null : index)}
                                    className="w-full text-left px-6 py-4 focus:outline-none"
                                >
                                    <div className="flex justify-between items-center">
                                        <h3 className="font-medium text-gray-900 dark:text-white">
                                            {item.question}
                                        </h3>
                                        <div
                                            className={`transition-transform duration-200 ${expandedIndex === index ? 'rotate-180' : ''}`}
                                        >
                                            <svg
                                                className="w-5 h-5 text-gray-500 dark:text-gray-400"
                                                fill="none"
                                                viewBox="0 0 24 24"
                                                stroke="currentColor"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M19 9l-7 7-7-7"
                                                />
                                            </svg>
                                        </div>
                                    </div>
                                </button>
                                {expandedIndex === index && (
                                    <div className="px-6 pb-4">
                                        <p className="text-gray-600 dark:text-gray-300">
                                            {item.answer}
                                        </p>
                                    </div>
                                )}
                            </div>
                        ))
                    )}
                </div>
            </div>
        </div>
    );
};

export default FAQ;